import classNames from 'classnames'
import type * as React from 'react'

import styles from './Card.module.scss'

/** Header band tint and, for every tone but `neutral`, the card's resting outline. */
export type CardTone = 'neutral' | 'info' | 'success' | 'warning'

export type CardProps = {
  /** Header band. Omit it for the blocks that have no header — the body then owns the whole card. */
  header?: React.ReactNode
  /** Renders as <section> by default; pass 'div' for a card that is not its own landmark. */
  as?: 'section' | 'div' | 'aside'
  /** Hover outline. On by default; turn it off for cards that are not a target of their own. */
  hoverable?: boolean
  expanded?: boolean
  className?: string
  headerClassName?: string
  bodyClassName?: string
  /** Set when the body is not the card's own scroll area (a card that only wraps a panel). */
  bare?: boolean
  tone?: CardTone
  /** Forwarded to the root so a widget tour can spotlight the card. */
  'data-tour-id'?: string
  ref?: React.Ref<HTMLElement>
  children: React.ReactNode
} & Omit<React.HTMLAttributes<HTMLElement>, 'className' | 'children'>

export const Card = ({
  header,
  as: Tag = 'section',
  hoverable = true,
  expanded = false,
  className,
  headerClassName,
  bodyClassName,
  bare = false,
  tone = 'neutral',
  ref,
  children,
  ...rest
}: CardProps) => (
  <Tag
    ref={ref as React.Ref<HTMLElement & HTMLDivElement>}
    className={classNames(
      styles.card,
      {
        [styles['card--hoverable']]: hoverable && !expanded,
        [styles['card--expanded']]: expanded,
        [styles[`card--${tone}`]]: tone !== 'neutral',
      },
      className
    )}
    {...rest}
  >
    {header ? (
      <header className={classNames(styles.header, headerClassName)}>{header}</header>
    ) : null}

    {bare ? (
      children
    ) : (
      <div className={classNames(styles.body, { [styles['body--only']]: !header }, bodyClassName)}>
        {children}
      </div>
    )}
  </Tag>
)

Card.displayName = 'Card'
